import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFlag } from '@unleash/proxy-client-react';

import { useGetSlackChannelsQuery } from '../../api/endpoints/ask-an-expert-endpoint';
import { useGetSettingsQuery } from '../../api/endpoints/settings-endpoint';
import { AppToggle } from '../../clients/unleash/app-toggle-names';
import { useAppDispatch, useAppSelector } from '../../redux/hooks/app-hooks';
import { useProject, useSources } from '../../redux/hooks/settings-hooks';
import { handleOpenAskAnExpert } from '../../redux/thunks/ask-an-expert-thunk';
import { sendMetrics } from '../../redux/thunks/metrics-thunk';
import { MixpanelEvent } from '../../services/mixpanel/types';

export interface UseAskAnExpertButtonProps {
    questionId?: string;
    question?: string;
}

export const useAskAnExpertButton = (props?: UseAskAnExpertButtonProps) => {
    const { t } = useTranslation('translations');

    const enabled = useFlag(AppToggle.ASK_AN_EXPERT);
    const { user } = useAppSelector((state) => state.auth);
    const { data: settingsData } = useGetSettingsQuery();

    const { sources } = useSources();
    const { project } = useProject();
    const { data: channels } = useGetSlackChannelsQuery(project, { skip: !project });

    const dispatch = useAppDispatch();

    // To avoid embarrassing situations, we block Ask-AI users from using the feature in customers' projects
    const shouldBlockAskAnExpert = user.user_org === 'ask-ai' && !settingsData?.ask_ai_project_ids?.includes(project);

    const onClick: React.MouseEventHandler = React.useCallback(
        (e) => {
            if (e instanceof MouseEvent) {
                e.stopPropagation();
            }

            if (!channels) {
                return;
            }

            dispatch(handleOpenAskAnExpert({ text: props?.question, id: props?.questionId }));
            dispatch(
                sendMetrics({
                    event: MixpanelEvent.OPEN_ASK_AN_EXPERT_DIALOG,
                    injectAnswersMetrics: {
                        sources,
                    },
                })
            );
        },
        [channels, dispatch, props, sources]
    );

    return {
        onClick,
        enabled,
        disabled: shouldBlockAskAnExpert || !channels,
        label: t('ask-an-expert.ask'),
    };
};
